import { useCallback } from 'react';
import { di } from 'react-magnetic-di';

import type { TemplateForm } from '@atlassian/proforma-common-core/form';
import {
	FormQuestionType,
	type ProformaForm,
} from '@atlassian/proforma-form-renderer/proforma-form';
import { convertToForm } from '@atlassian/proforma-form-renderer/legacy-converters/utils';

import type { Forms } from '../../../../common/types/forms';
import {
	useSetForms,
	useSetJiraFieldData,
} from '../../../../controllers/request-create-context-provider';
import { useGetFieldDataService, useGetFormChoicesService } from '../../../../services';

/**
 * Hook to convert a TemplateForm/ProformaForm to the Form format used by FormRenderer.
 * For request-create, this is simpler than request-view because:
 * - We always use EDIT mode (no form status checking)
 * - We always apply default values
 */
export const useRefreshForm = () => {
	di(useGetFieldDataService, useGetFormChoicesService, useSetJiraFieldData, useSetForms);

	const {
		fetch: fetchFieldData,
		loading: isFetchingFieldData,
		error: fetchFieldDataError,
	} = useGetFieldDataService();
	const {
		fetch: fetchFormChoices,
		loading: isFetchingFormChoices,
		error: fetchFormChoicesError,
	} = useGetFormChoicesService();
	const setJiraFieldData = useSetJiraFieldData();
	const setForms = useSetForms();

	const refreshForm = useCallback(
		async (form: TemplateForm): Promise<Forms | undefined> => {
			const hasJiraFields = Object.values(form.design.questions).some(
				(question) => question.jiraField,
			);
			const hasDataConnectionFields = Object.values(form.design.questions).some(
				(question) => question.dcId,
			);
			const shouldFetchFormChoices = hasJiraFields || hasDataConnectionFields;
			const shouldFetchFieldData = hasJiraFields;

			// Fetch enrichment data (field data and form choices) individually, catching errors
			// so the form still renders gracefully without enrichment when APIs fail (e.g. 403).
			// Error reporting and user notifications are already handled in proformaGasAnalyticsFetch.
			const [fieldData, formChoices] = await Promise.all([
				shouldFetchFieldData
					? fetchFieldData({
							templateFormId: form.id,
							fieldDataRequest: {
								fields: Object.values(form.design.questions)
									.filter((question) => question.jiraField)
									.map((question) => ({
										fieldKey: question.jiraField || '',
										...(question.type === FormQuestionType.RichTextParagraph && {
											format: 'adf',
										}),
									})),
							},
						}).catch(() => undefined)
					: Promise.resolve(undefined),
				shouldFetchFormChoices
					? fetchFormChoices().catch(() => undefined)
					: Promise.resolve(undefined),
			]);

			if (fieldData) {
				setJiraFieldData(new Map(Object.entries(fieldData)));
			}

			// For request-create, always apply default values (4th param = true)
			// Cast TemplateForm to ProformaForm - the uuid field is optional in common-core but required in form-renderer types
			const convertedForm = convertToForm(form as ProformaForm, fieldData, formChoices, true);
			const forms: Forms = {
				form: convertedForm,
				proformaForm: form,
			};
			setForms(forms);
			return forms;
		},
		[fetchFieldData, fetchFormChoices, setJiraFieldData, setForms],
	);

	return {
		fetchFieldDataError,
		fetchFormChoicesError,
		refreshForm,
		isFetchingFieldData,
		isFetchingFormChoices,
	};
};
